import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import { useAuthActions } from 'src/hooks/useAuthActions';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useOrganizationRole } from 'src/hooks/useAuthSession';
import { useAppStore } from 'src/states/app';
import { ActionContext, ActionEntry, visibleActions } from './registries/actionRegistry';
import { RecordHit, useSearchRecords } from './useSearchRecords';
import { RouteEntry, visibleRoutes } from './registries/routeIndex';
import { SettingEntry, visibleSettings } from './registries/settingsIndex';
import { scoreMatch } from './score';
import { PaletteItem, PaletteSection, LocalSectionId, SECTION_ORDER } from './types';

export interface BuildSectionsInput {
  query: string;
  routes: RouteEntry[];
  actions: ActionEntry[];
  settings: SettingEntry[];
  translate: (key: string) => string;
  runAction: (entry: ActionEntry) => void;
}

function rank(items: PaletteItem[]): PaletteItem[] {
  // Stable within equal scores: sort() is stable in every engine we target,
  // so registry order breaks ties and the list never shuffles between renders.
  return [...items].sort((left, right) => right.score - left.score);
}

/**
 * Sections are emitted in SECTION_ORDER regardless of score. This is what keeps the
 * row under the user's cursor from moving when Phase 2's record sections arrive.
 */
export function buildSections(input: BuildSectionsInput): PaletteSection[] {
  const { query, routes, actions, settings, translate, runAction } = input;

  const collected: Record<LocalSectionId, PaletteItem[]> = { pages: [], actions: [], settings: [] };

  for (const entry of routes) {
    const label = translate(entry.labelKey);
    const score = scoreMatch(label, query);
    if (score === null) continue;
    collected.pages.push({ id: `pages:${entry.id}`, section: 'pages', label, path: entry.path, score });
  }

  for (const entry of actions) {
    const label = translate(entry.labelKey);
    const score = scoreMatch(label, query);
    if (score === null) continue;
    collected.actions.push({
      id: `actions:${entry.id}`,
      section: 'actions',
      label,
      run: () => runAction(entry),
      score,
    });
  }

  for (const entry of settings) {
    const label = translate(entry.labelKey);
    const score = scoreMatch(label, query);
    if (score === null) continue;
    collected.settings.push({ id: `settings:${entry.id}`, section: 'settings', label, path: entry.path, score });
  }

  return SECTION_ORDER.map((id) => ({ id, items: rank(collected[id]) })).filter(
    (section) => section.items.length > 0
  );
}

export function flattenItems(sections: PaletteSection[]): PaletteItem[] {
  return sections.flatMap((section) => section.items);
}

export function buildRecordSections(hits: RecordHit[], translate: (key: string) => string): PaletteSection[] {
  const byType = new Map<string, PaletteItem[]>();

  for (const hit of hits) {
    // Server order is the ranking — re-scoring here would fight it and could
    // reorder rows under the cursor as later keystrokes resolve.
    const items = byType.get(hit.type) ?? [];
    items.push({
      id: `${hit.type}:${hit.id}`,
      section: hit.type,
      label: hit.title,
      sublabel: hit.subtitle,
      path: hit.targetRoute,
      params: hit.targetParams,
      score: 0,
    });
    byType.set(hit.type, items);
  }

  // The section carries its own resolved title, so the modal renders `section.title`
  // uniformly instead of branching on whether an id is local or a record type.
  return [...byType.entries()].map(([type, items]) => ({ id: type, title: translate(type), items }));
}

/** React binding over buildSections. Everything testable lives in buildSections. */
export function useGlobalSearch(query: string): {
  sections: PaletteSection[];
  items: PaletteItem[];
  records: ReturnType<typeof useSearchRecords>;
} {
  const { t } = useTranslation();
  const role = useOrganizationRole();
  const { isPlatformAdmin } = useAuthPermissions();
  const { signOut } = useAuthActions();

  const { theme, locale, fullscreen, setTheme, setLocale, setFullscreen } = useAppStore(
    (state) => ({
      theme: state.theme,
      locale: state.locale,
      fullscreen: state.fullscreen,
      setTheme: state.setTheme,
      setLocale: state.setLocale,
      setFullscreen: state.setFullscreen,
    }),
    shallow
  );

  const actionContext: ActionContext = useMemo(
    () => ({ theme, setTheme, locale, setLocale, fullscreen, setFullscreen, signOut }),
    [theme, setTheme, locale, setLocale, fullscreen, setFullscreen, signOut]
  );

  const sections = useMemo(
    () =>
      buildSections({
        query,
        routes: visibleRoutes(role, isPlatformAdmin),
        actions: visibleActions(role, isPlatformAdmin),
        settings: visibleSettings(role, isPlatformAdmin),
        translate: (key) => t(`globalSearch.entries.${key}`),
        runAction: (entry) => entry.run(actionContext),
      }),
    [query, role, isPlatformAdmin, t, actionContext]
  );

  const records = useSearchRecords(query);

  const recordSections = useMemo(
    () => buildRecordSections(records.hits, (key) => t(`globalSearch.recordSections.${key}`)),
    [records.hits, t]
  );

  // Local sections first, always. This is the invariant from Phase 1: a record
  // arriving 250ms later must never move the row under the user's cursor.
  const allSections = useMemo(() => [...sections, ...recordSections], [sections, recordSections]);

  return { sections: allSections, items: flattenItems(allSections), records };
}
